'use client';

import * as HoverCard from '@radix-ui/react-hover-card';
import React, { useState } from 'react';
import { WebsiteSignalDateGroup } from '@/types/signals';
import { cn } from '@/app/lib/utils';
import WebsiteSignalActivityItem from './WebsiteSignalActivityItem';
import { Separator } from '../ui/Separator';
import { useSalesforceEmbed } from '@/app/context/salesforce-embed';

type WebsiteSignalActivityPopoverProps = React.PropsWithChildren<{
  dateGroup: WebsiteSignalDateGroup;
  header: React.ReactNode | ((close: () => void) => React.ReactNode);
  disabled?: boolean;
}>;

const WebsiteSignalActivityPopover = ({
  dateGroup,
  header,
  children,
  disabled = false,
}: WebsiteSignalActivityPopoverProps) => {
  const salesforceEmbed = useSalesforceEmbed();
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  if (disabled || salesforceEmbed.isEmbed) {
    return <>{children}</>;
  }

  return (
    <HoverCard.Root open={open} onOpenChange={setOpen} openDelay={0} closeDelay={0}>
      <HoverCard.Trigger asChild>
        <div>{children}</div>
      </HoverCard.Trigger>
      <HoverCard.Portal>
        <HoverCard.Content
          side="left"
          align="start"
          sideOffset={-4}
          collisionPadding={16}
          className={cn(
            'pointer-events-auto z-[999] w-[min(520px,calc(100vw-32px))] overflow-hidden rounded-[8px] border border-border bg-background shadow-[0_16px_40px_rgba(14,18,27,0.18)]',
            'origin-[var(--radix-hover-card-content-transform-origin)] duration-150',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
            'data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95',
            'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2'
          )}
        >
          <div className="px-4 py-4">{typeof header === 'function' ? header(close) : header}</div>
          <Separator />
          <div className="max-h-[520px] overflow-y-auto">
            {dateGroup.signals.map(signal => (
              <WebsiteSignalActivityItem key={signal.id} signal={signal} expanded />
            ))}
          </div>
          <HoverCard.Arrow className="fill-background" />
        </HoverCard.Content>
      </HoverCard.Portal>
    </HoverCard.Root>
  );
};

export default WebsiteSignalActivityPopover;
